import { Button, CircularProgress, Dialog, MenuItem, Select, TextField } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { useState } from 'react';
import constants from '../config/constants';
import { useLanguage } from '../context/language.context';
import { getLocalizedErrorMessage } from '../utils/functions';

/**
 * GH-688 (Keren, 8.9.2026) — "פגישה ראשונה למנוי/ה חדש/ה".
 * Until now an expert could only book with a member who had already booked them (the dialogs open
 * from an existing session row). This dialog resolves a member by EMAIL via the expert panel
 * (`POST /instructor/admin/member/lookup` — active member, same is_global side, no upcoming session
 * with this expert), lets the expert pick one of their 1:1 services, and hands (member_id, name,
 * service) to the SAME AppointmentScheduleDialog used for existing members. Single session only (V1).
 */
type Props = {
    open: boolean;
    onClose: () => void;
    services: any[];
    onMemberResolved: (member_id: string, member_name: string, service: any) => void;
};

const NewMemberSessionDialog = ({ open, onClose, services, onMemberResolved }: Props) => {
    const { t, dir } = useLanguage();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [found, setFound] = useState<{ member_id: string; first_name: string; past_sessions?: number } | null>(null);
    // Budget-management (wellness) services are never bookable from the expert panel — the
    // member books them through their budget offer (the backend rejects them too).
    const activeServices = (services || []).filter((s) => s?.active !== false && !s?.wellness_session);
    const [serviceId, setServiceId] = useState('');

    const reset = () => {
        setEmail('');
        setError('');
        setFound(null);
        setServiceId('');
        setLoading(false);
    };
    const close = () => {
        reset();
        onClose();
    };

    const lookup = async () => {
        setError('');
        setFound(null);
        if (!email.trim() || !email.includes('@')) {
            setError(t('newMemberInvalidEmail'));
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${constants.API_URL}/instructor/admin/member/lookup`, { email: email.trim() });
            setFound(res.data);
            if (activeServices.length === 1) setServiceId(activeServices[0]._id);
        } catch (e) {
            setError(getLocalizedErrorMessage(e as AxiosError, 'he') || t('newMemberNotFound'));
        } finally {
            setLoading(false);
        }
    };

    const proceed = () => {
        if (!found || !serviceId) return;
        const service = activeServices.find((s) => s._id === serviceId);
        if (!service) return;
        const name = found.first_name || email.trim();
        reset();
        onMemberResolved(found.member_id, name, service);
    };

    return (
        <Dialog open={open} onClose={close} PaperProps={{ style: { borderRadius: 10 } }}>
            <div dir={dir} className='py-4 px-8 w-[450px] max-w-full flex flex-col gap-5 text-start'>
                <div className='text-center'>
                    <h2 className='text-lg font-bold'>{t('newMemberSessionTitle')}</h2>
                    <p className='text-sm text-gray-600 mt-1'>{t('newMemberSessionHint')}</p>
                </div>

                <div className='flex flex-col gap-2'>
                    <label className='font-medium'>{t('newMemberEmailLabel')}</label>
                    <div className='flex flex-row gap-2 items-center'>
                        <TextField
                            size='small'
                            type='email'
                            fullWidth
                            value={email}
                            disabled={loading || Boolean(found)}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !found) lookup();
                            }}
                            inputProps={{ dir: 'ltr', style: { fontFamily: 'Open Sans' } }}
                            placeholder='name@company.com'
                        />
                        {found ? (
                            <Button variant='text' onClick={() => { setFound(null); setServiceId(''); setError(''); }}>
                                {t('newMemberChangeEmail')}
                            </Button>
                        ) : (
                            <Button variant='outlined' onClick={lookup} disabled={loading} style={{ whiteSpace: 'nowrap' }}>
                                {loading ? <CircularProgress size={18} /> : t('newMemberCheck')}
                            </Button>
                        )}
                    </div>
                    {error ? <p className='text-sm text-red-600'>{error}</p> : null}
                </div>

                {found ? (
                    <>
                        <div className='rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm'>
                            {t('newMemberFound')} <b>{found.first_name || email.trim()}</b>
                        </div>
                        {found.past_sessions ? (
                            // Informational only (not an error): the member already met this expert —
                            // they're in the sessions table too, where a follow-up can be booked from the row.
                            <div className='rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-900'>
                                {t('newMemberHasPastSessions').replace('{n}', String(found.past_sessions))}
                            </div>
                        ) : null}
                        <div className='flex flex-col gap-2'>
                            <label className='font-medium'>{t('newMemberServiceLabel')}</label>
                            <Select
                                size='small'
                                value={serviceId}
                                displayEmpty
                                onChange={(e) => setServiceId(String(e.target.value))}
                            >
                                <MenuItem value='' disabled>
                                    {t('newMemberServicePlaceholder')}
                                </MenuItem>
                                {activeServices.map((s) => (
                                    <MenuItem key={s._id} value={s._id}>
                                        {s.title?.he || s.title?.en || ''}
                                        {s.duration ? ` · ${s.duration} ${t('minutes')}` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                            {!activeServices.length ? <p className='text-sm text-red-600'>{t('newMemberNoServices')}</p> : null}
                        </div>
                    </>
                ) : null}

                <div className='flex flex-row gap-2 justify-end mt-2'>
                    <Button variant='text' onClick={close}>
                        {t('cancel')}
                    </Button>
                    <Button variant='contained' onClick={proceed} disabled={!found || !serviceId}>
                        {t('newMemberContinue')}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

export default NewMemberSessionDialog;
